import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { PlanCards } from "../components/PlanCards";
import { api, errMsg, formatDate } from "../lib/api";
import { payWithRazorpay } from "../lib/payments";
import { useAuth } from "../context/AuthContext";

export default function Pricing() {
  const { user, login, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [plan, setPlan] = useState(params.get("plan") || "yearly");
  const [ready, setReady] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/payments/config").then((r) => setReady(r.data.razorpay)); }, []);

  const pay = async () => {
    if (!user) return login(`/pricing?plan=${plan}`);
    setBusy(true);
    try {
      await payWithRazorpay(plan);
      await refresh();
      toast.success("Payment successful — your PDFs are unlocked!");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response ? errMsg(e) : e.message);
    }
    setBusy(false);
  };

  return (
    <main className="max-w-5xl mx-auto px-5 sm:px-8 py-14 relative z-10" data-testid="pricing-page">
      <p className="text-xs font-mono uppercase tracking-[0.25em] text-emerald-400">Unlock downloads</p>
      <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">Pick a plan</h1>
      {user?.has_access && (
        <p className="mt-4 text-sm text-emerald-400" data-testid="pricing-active-note">You're unlocked until {formatDate(user.access_until)}. Buying again adds more time.</p>
      )}
      <div className="mt-10"><PlanCards selected={plan} onSelect={setPlan} /></div>
      <div className="mt-12 rounded-2xl border border-white/10 bg-[#0c0e14] p-8" data-testid="razorpay-checkout-box">
        <h2 className="text-base md:text-lg font-semibold">Pay securely with Razorpay</h2>
        <p className="mt-1 text-sm text-slate-400">UPI · Cards · Netbanking · Wallets — charged in INR</p>
        {ready === false && <p className="mt-4 text-sm text-amber-400" data-testid="razorpay-unavailable">Payments are being set up. Please check back shortly.</p>}
        <Button onClick={pay} disabled={busy || (user && !ready)} data-testid="checkout-pay-btn"
          className="mt-8 rounded-full h-12 px-8 bg-emerald-500 text-black font-semibold hover:bg-emerald-400 hover:text-black">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {user ? "Pay with Razorpay" : "Sign in to continue"}
        </Button>
        <p className="mt-4 text-xs text-slate-500 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> One-time payment for the selected period. No auto-renewal.</p>
      </div>
    </main>
  );
}
